import React, { useEffect, useState } from 'react';
import { adminApi } from '../services/api';
import { Link2, Plus, Trash2, Loader2, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Linked staff: email addresses from another practice that may sign in to
 * this tenant. Only a PMP super-admin can grant or remove one. The tenant's
 * own administrators still create the user (role, permissions) in
 * Settings → Users, and the person signs in to each practice separately.
 */
export default function LinkedStaffPanel({ tenantId }) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getTenantMemberships(tenantId);
      setMemberships(res.data?.memberships || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not load linked staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (tenantId) load(); }, [tenantId]);

  const grant = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      const res = await adminApi.grantTenantMembership(tenantId, { email: email.trim(), note: note.trim() || undefined });
      setMemberships(res.data?.memberships || []);
      setEmail('');
      setNote('');
      toast.success(res.data?.reactivated ? 'Link restored' : 'Address linked to this practice');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not link this address');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (m) => {
    if (!window.confirm(`Remove ${m.email}'s access to this practice? Any session they hold here ends now. Their own practice is not affected.`)) return;
    setRemoving(m.id);
    try {
      const res = await adminApi.revokeTenantMembership(tenantId, m.id);
      setMemberships(res.data?.memberships || []);
      toast.success('Link removed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove the link');
    } finally {
      setRemoving(null);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const active = memberships.filter((m) => m.status === 'active');
  const revoked = memberships.filter((m) => m.status !== 'active');

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Link2 className="w-5 h-5 mr-2 text-blue-600" />
          Linked staff from other practices
        </h3>
        <span className="text-xs text-gray-500 flex items-center"><ShieldCheck className="w-4 h-4 mr-1" /> Super-admin only</span>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Lets an email address whose own domain belongs to another practice sign in here. The practice administrator still
        adds the person (with a role) under Settings → Users, and the person signs in to each practice separately. Mailboxes
        are never shared across practices by this link.
      </p>

      <form onSubmit={grant} className="flex flex-col sm:flex-row gap-2 mb-5">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="staff@otherpractice.com.au"
          className="input flex-1"
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
          className="input flex-1"
          maxLength={500}
        />
        <button type="submit" className="btn btn-primary whitespace-nowrap" disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          <span className="ml-1">Link address</span>
        </button>
      </form>

      {loading ? (
        <div className="text-center py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : active.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No linked staff.</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {active.map((m) => (
            <div key={m.id} className="flex items-start justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{m.email}</p>
                <p className="text-xs text-gray-500">
                  Linked {fmt(m.grantedAt)}{m.grantedBy ? ` by ${m.grantedBy}` : ''}
                  {' · '}{m.lastUsedAt ? `last sign-in ${fmt(m.lastUsedAt)}` : 'never signed in'}
                  {m.directory ? ' · Microsoft directory pinned' : ''}
                </p>
                {m.note && <p className="text-xs text-gray-600 mt-1">{m.note}</p>}
              </div>
              <button
                type="button"
                onClick={() => revoke(m)}
                disabled={removing === m.id}
                className="p-2 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
                title="Remove access"
              >
                {removing === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      )}

      {revoked.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-gray-500 cursor-pointer">Previously linked ({revoked.length})</summary>
          <ul className="mt-2 space-y-1">
            {revoked.map((m) => (
              <li key={m.id} className="text-xs text-gray-500">
                {m.email} — removed {fmt(m.revokedAt)}{m.revokedBy ? ` by ${m.revokedBy}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
